import { useLazyFileLoad } from "@/shared/hooks";
import React, { useRef } from "react";
import { Play } from "react-bootstrap-icons";
import { LazyMediaProps } from "./lazy-media.props";

export const LazyMedia = ({
  src,
  alt = "",
  type,
  height = 250,
  width = 250,
  style,
  rounded,
  isSelected = false,
  onClick,
  handleRightClick,
}: LazyMediaProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLImageElement | HTMLVideoElement>(null);

  const {
    url: objectUrl,
    loading: isLoading,
    error,
  } = useLazyFileLoad(containerRef, src, { threshold: 0.1 });

  const placeholderSVG = `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 100 100' fill='none'%3e%3cstyle%3etitle%7bfont-size:0.8em%7d%3c/style%3e%3cg fill='currentColor'%3e%3c/g%3e%3c/svg%3e`;

  if (type === "image") {
    return (
      <div
        ref={containerRef}
        style={{
          position: "relative",
          display: "inline-block",
          borderRadius: "inherit",
        }}
      >
        <img
          draggable={true}
          data-is-draggable-file
          onContextMenu={handleRightClick}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            objectUrl && onClick?.(objectUrl);
          }}
          ref={mediaRef as React.RefObject<HTMLImageElement>}
          src={objectUrl || placeholderSVG}
          alt={alt}
          height={height}
          width={width}
          decoding="async"
          className={rounded}
          style={{
            maxWidth: "100%",
            margin: 0,
            objectFit: "cover",
            cursor: onClick ? "pointer" : "default",
            backgroundColor: "#f0f0f0",
            opacity: objectUrl ? 1 : 0.6,
            transition: "opacity 0.3s ease",
            ...style,
          }}
        />
        {isSelected && (
          <div
            className={rounded}
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0, 123, 255, 0.35)",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-is-draggable-file
      draggable={true}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        objectUrl && onClick?.(objectUrl);
      }}
      onContextMenu={handleRightClick}
      style={{ cursor: onClick ? "pointer" : "default", position: "relative" }}
    >
      <video
        ref={mediaRef as React.RefObject<HTMLVideoElement>}
        src={objectUrl || undefined}
        height={height}
        width={width}
        muted
        playsInline
        className={rounded}
        style={{
          verticalAlign: "top",
          margin: 0,
          overflow: "hidden",
          position: "relative",
          objectFit: "cover",
          opacity: objectUrl ? 1 : 0.6,
          transition: "opacity 0.3s ease",
          ...style,
        }}
      />
      {objectUrl && !isLoading && !error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Play size={32} color="white" />
        </div>
      )}
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div className="spinner-border spinner-border-sm text-light" />
        </div>
      )}
      {error && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            backgroundColor: "rgba(0,0,0,0.7)",
          }}
        >
          ❌
        </div>
      )}
      {isSelected && (
        <div
          className={rounded}
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(0, 123, 255, 0.35)",
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
};
